import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { BackendTypeaheadSelectInput } from '@/components/form/baseInputs/BackendTypeaheadSelectInput/BackendTypeaheadSelectInput';
import { Button } from '@/components/form/baseInputs/Button';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { DOCUMENT_TYPE_DISPLAY_NAMES } from '@/modules/documents/documents.constants';
import { DocumentType } from '@/types/schema';
import { getIndefiniteArticle } from '@/utils/formatting/strings';

import { useDocumentsTypeaheadForPrompt } from '../../hooks/useDocumentsTypeaheadForPrompt';

interface DocumentSelectorRowProps {
  selectedDocumentId: string;
  documentType: DocumentType;
  onSelectDocument: (_event: unknown, value: string | null) => void;
  onGeneratePreview: () => void;
  isDisabled: boolean;
}

export function DocumentSelectorRow({
  selectedDocumentId,
  documentType,
  onSelectDocument,
  onGeneratePreview,
  isDisabled,
}: DocumentSelectorRowProps) {
  const { typeaheadProps } = useDocumentsTypeaheadForPrompt(
    documentType as unknown as DocumentType,
    selectedDocumentId
  );

  const documentTypeDisplay = useMemo(() => {
    return DOCUMENT_TYPE_DISPLAY_NAMES[documentType];
  }, [documentType]);

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack>
        <Typography variant="title-s">Preview document summary</Typography>
        <Typography variant="body-xs">
          Choose {getIndefiniteArticle(documentTypeDisplay)} &quot;
          {documentTypeDisplay}&quot; document to test your prompt
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <Box sx={{ flex: 1 }}>
          <BackendTypeaheadSelectInput<string>
            {...typeaheadProps}
            label="Document to preview"
            hideLabel
            placeholder="Select document"
            noOptionsText={`No ${documentTypeDisplay}s found`}
            value={selectedDocumentId}
            onChange={onSelectDocument}
            groupBy={(option) => option.groupName ?? ''}
          />
        </Box>
        <Button
          size="sm"
          variant="secondary"
          startIcon={EyeIcon}
          onClick={onGeneratePreview}
          disabled={isDisabled}
        >
          Preview
        </Button>
      </Stack>
    </Stack>
  );
}
